/* eslint-disable react-hooks/exhaustive-deps */
import { MarkerType } from '@eagle/api-types';
import { Marker as UiMarker } from '@ethings/ui-toolkit/map';
import { useTheme } from '@mui/material';
import type { Direction, PointExpression } from 'leaflet';
import L from 'leaflet';
import { isString } from 'lodash';
import {
  CSSProperties,
  ReactNode,
  type JSX,
} from 'react';
import { useFetchOneCache, useMapContext, usePromiseConditional } from '../../hooks';
import { CommonEntity, Undefinable } from '../../types';
import { CacheDataTypes } from '../../types/cache';
import { getMarkerTemplate } from '../../util/maps';
import { FieldLoading } from '../field';
import { Icon } from '../icons/widget-icon';

export interface MapMarkerTooltipProps {
  enableClick?: boolean;
  background?: string;
  color?: string;
  direction?: Direction;
  offset?: PointExpression;
  permanent?: boolean;
  styles?: CSSProperties;
}

export interface MapMarkerProps {
  cacheDataType: CacheDataTypes;
  disableMarkerClick?: boolean;
  'data-testid'?: string;
  entityId: string;
  position: L.LatLngExpression;
  background?: string;
  borderColor?: string;
  markerType?: MarkerType;
  indicator?: string;
  icon?: ReactNode | string;
  iconColor?: string;
  image?: string;
  tooltip?: MapMarkerTooltipProps;
  tooltipContent?: JSX.Element;
  iconSize?: [number, number];
}

const toPosition = (pos: L.LatLngExpression): [number, number] => {
  if (Array.isArray(pos)) return pos as [number, number];
  if (pos instanceof L.LatLng) return [pos.lat, pos.lng];
  return [pos.lat, pos.lng];
};

const toOffset = (point: PointExpression | undefined, fallback: [number, number]): [number, number] => {
  if (!point) return fallback;
  if (Array.isArray(point)) return point as [number, number];
  if (typeof point === 'number') return [point, point];
  return [point.x, point.y];
};

/**
  * NATIVE FUNCTION: Display map marker with entity data
  */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function MapMarker<T extends CommonEntity<unknown>>({ cacheDataType, disableMarkerClick, entityId, position, background, borderColor, markerType, icon, iconColor, image, indicator, tooltip, tooltipContent, iconSize, ...props }: MapMarkerProps): JSX.Element {
  const { handleMarkerClick } = useMapContext();
  const entityCache = useFetchOneCache(cacheDataType);
  const theme = useTheme();
  const marker = markerType || MarkerType.PIN;
  const markerBackground = background || theme.marker.background;
  const iconComponent = isString(icon) ? <Icon icon={icon} style={{ color: iconColor }} /> : icon;
  const leafletIcon = getMarkerTemplate(marker, iconComponent, image, markerBackground, iconColor, indicator, borderColor, iconSize);
  const [entityData] = usePromiseConditional<Undefinable<T>>(() => entityCache.one(entityId), () => true, [entityId]);

  const tooltipRendered = tooltipContent ?? (
    entityData
      ? <span data-chromatic="ignore" data-testid={props['data-testid']} title={entityData.display}>{entityData.display}</span>
      : <FieldLoading />
  );

  return (
    <UiMarker
      position={toPosition(position)}
      leafletIcon={leafletIcon}
      onClick={disableMarkerClick ? undefined : () => handleMarkerClick(entityId)}
      tooltip={tooltipRendered}
      tooltipOptions={{
        permanent: tooltip?.permanent ?? true,
        direction: (tooltip?.direction || 'center') as 'top' | 'right' | 'bottom' | 'left' | 'center',
        offset: tooltip?.offset ? toOffset(tooltip.offset, [0, 0]) : (marker === MarkerType.PIN ? [0, 37] : [0, 45]),
        enableClick: tooltip?.enableClick,
        background: tooltip?.background,
        color: tooltip?.color,
        styles: tooltip?.styles,
      }}
    />
  );
}
